import { AxiosResponse } from "axios";
import { endPoints, tableVariant } from "utils/constants";
import { crudService } from "services/crud.service";
import { NotificationManager } from "react-notifications";
import { defaultMetaObj, initialTaskState } from "store/constants";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const { leadTaskEndPoint } = endPoints;
const { leadTasks, tasks } = tableVariant;

export const fetchAllTasks = createAsyncThunk(
  "leads/fetchAllTasks",
  async (
    {
      page,
      limit,
      searchParamsObj = {},
    }: { page: number; limit?: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      limit,
      endpoint: leadTaskEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllTasksForDashboard = createAsyncThunk(
  "tasks/fetchAllTasksForDashboard",
  async (
    { searchParamsObj = {} }: { searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: 1,
      endpoint: leadTaskEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      let response: AxiosResponse = await crudService.getAll(paginatedUrl);

      if (response?.data?.meta?.total_count > 20) {
        const paginatedUrlForTotalCount = getPaginatedUrlWithSearchParams({
          pageNumber: 1,
          limit: response?.data?.meta?.total_count,
          endpoint: leadTaskEndPoint,
          searchParams: {
            ...searchParamsObj,
          },
        });

        response = await crudService.getAll(paginatedUrlForTotalCount);
      }

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteTask = createAsyncThunk(
  "tasks/deleteTask",
  async ({ taskId }: any, { rejectWithValue }) => {
    try {
      const response: any = await crudService.delete(leadTaskEndPoint, taskId);

      if (response.status === 204) {
        NotificationManager.success("Task Deleted Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const addNewTask = createAsyncThunk(
  "tasks/addNewTask",
  async (newLead: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${leadTaskEndPoint}/`,
        newLead
      );

      if (response.status === 201) {
        NotificationManager.success("Task Created Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const updateTask = createAsyncThunk(
  "tasks/updateTask",
  async ({ updatedTask, taskId, type = null }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        leadTaskEndPoint,
        taskId,
        updatedTask
      );
      if (response.status === 204) {
        const messageToDisplay =
          type === leadTasks || type === tasks
            ? "Task Closed Successfully"
            : "Task updated Successfully.";
        NotificationManager.success(messageToDisplay);
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

const taskSlice = createSlice({
  name: "tasks",
  initialState: initialTaskState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch all tasks
    builder
      .addCase(fetchAllTasks.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllTasks.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.tasks = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllTasks.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.tasks = [];
          state.meta = defaultMetaObj;
        }
      );

    // fetch all dashboard tasks
    builder
      .addCase(fetchAllTasksForDashboard.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllTasksForDashboard.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.dashboardTasks = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllTasksForDashboard.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.dashboardTasks = [];
          state.meta = defaultMetaObj;
        }
      );

    // delete Task
    builder
      .addCase(deleteTask.pending, (State) => {
        State.loading = true;
      })
      .addCase(deleteTask.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
      })
      .addCase(
        deleteTask.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // add new task
    builder
      .addCase(addNewTask.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(addNewTask.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
      })
      .addCase(addNewTask.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    //   update task
    builder
      .addCase(updateTask.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateTask.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updateTask.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );
  },
});

export const taskReducer = taskSlice.reducer;
